import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email_address: string;
  role_type_id: number;
  company?: string;
  phone?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  userRole: 'field-worker' | 'supervisor' | null;
  kioskMode: boolean;
  kioskJhaId: string | null;
  _hasHydrated: boolean;

  // Actions
  setUser: (user: User, token: string, refreshToken?: string) => void;
  setToken: (token: string) => void;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
  enterKioskMode: (jhaId: string) => void;
  exitKioskMode: () => void;
  setHasHydrated: (state: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      userRole: null,
      kioskMode: false,
      kioskJhaId: null,
      _hasHydrated: false,

      setUser: (user, token, refreshToken) => {
        // Map role_type_id to role: 1-4 = supervisor, 5+ = field-worker
        const role = user.role_type_id <= 4 ? 'supervisor' : 'field-worker';

        set({
          user,
          token,
          refreshToken: refreshToken || null,
          isAuthenticated: true,
          userRole: role,
        });
      },

      setToken: (token) => {
        set({ token });
      },

      logout: () => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          userRole: null,
          kioskMode: false,
          kioskJhaId: null,
        });
      },

      updateUser: (updates) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        }));
      },

      enterKioskMode: (jhaId) => {
        set({ kioskMode: true, kioskJhaId: jhaId });
      },

      exitKioskMode: () => {
        set({ kioskMode: false, kioskJhaId: null });
      },

      setHasHydrated: (state) => {
        set({
          _hasHydrated: state,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        userRole: state.userRole,
        kioskMode: state.kioskMode,
        kioskJhaId: state.kioskJhaId,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);
